import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { Patient, HumanName, Address, ContactPoint, Resource } from '@medplum/fhirtypes';

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  addressLine: string;
  city: string;
  state: string;
  postalCode: string;
}

export function getContactValue(telecom: ContactPoint[] | undefined, system: string): string {
  return telecom?.find(t => t.system === system)?.value || '';
}

export function extractProfileData(profile: Patient | undefined): ProfileData {
  if (!profile) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      birthDate: '',
      addressLine: '',
      city: '',
      state: '',
      postalCode: '',
    };
  }

  const name = profile.name?.[0];
  const address = profile.address?.[0];

  return {
    firstName: name?.given?.[0] || '',
    lastName: name?.family || '',
    email: getContactValue(profile.telecom, 'email'),
    phone: getContactValue(profile.telecom, 'phone'),
    birthDate: profile.birthDate || '',
    addressLine: address?.line?.[0] || '',
    city: address?.city || '',
    state: address?.state || '',
    postalCode: address?.postalCode || '',
  };
}

export function buildUpdatedProfile(profile: Patient, data: ProfileData): Patient {
  // Preserve existing telecom entries that aren't email/phone
  const existingTelecom = profile.telecom?.filter(
    (t: ContactPoint) => t.system !== 'email' && t.system !== 'phone'
  ) || [];

  const telecom: ContactPoint[] = [...existingTelecom];
  if (data.email) {
    telecom.push({ system: 'email', value: data.email });
  }
  if (data.phone) {
    telecom.push({ system: 'phone', value: data.phone });
  }

  // Preserve existing name properties
  const existingName = profile.name?.[0] || {};
  const name: HumanName = {
    ...existingName,
    given: data.firstName ? [data.firstName] : existingName.given,
    family: data.lastName || existingName.family,
  };

  // Preserve existing address properties
  const existingAddress = profile.address?.[0] || {};
  const address: Address = {
    ...existingAddress,
    line: data.addressLine ? [data.addressLine] : existingAddress.line,
    city: data.city || existingAddress.city,
    state: data.state || existingAddress.state,
    postalCode: data.postalCode || existingAddress.postalCode,
  };

  return {
    ...profile,
    name: [name],
    birthDate: data.birthDate || profile.birthDate,
    address: [address],
    telecom: telecom.length > 0 ? telecom : profile.telecom,
  };
}

export function useProfile() {
  const medplum = useMedplum();
  const baseProfile = useMedplumProfile() as Resource | undefined;
  const [freshProfile, setFreshProfile] = useState<Patient | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch fresh profile data from server
  const refreshProfile = useCallback(async () => {
    if (!baseProfile?.id || !baseProfile?.resourceType) return;

    try {
      setIsLoading(true);
      const fresh = await medplum.readResource(
        baseProfile.resourceType as 'Patient',
        baseProfile.id
      );
      setFreshProfile(fresh as Patient);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, baseProfile?.id, baseProfile?.resourceType]);

  // Load fresh profile on mount and when base profile changes
  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  // Use fresh profile if available, otherwise fall back to base
  const profile = freshProfile || (baseProfile as Patient | undefined);

  const profileData = useMemo(() => extractProfileData(profile), [profile]);

  const isComplete = useMemo(() => {
    return !!(
      profileData.firstName &&
      profileData.lastName &&
      profileData.email &&
      profileData.birthDate
    );
  }, [profileData]);

  const updateProfile = useCallback(async (data: ProfileData) => {
    if (!profile) {
      setError('No profile found');
      return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updatedProfile = buildUpdatedProfile(profile, data);
      const result = await medplum.updateResource(updatedProfile);
      setFreshProfile(result as Patient);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [medplum, profile]);

  return {
    profile,
    profileData,
    isComplete,
    isLoading,
    isSaving,
    error,
    updateProfile,
    refreshProfile,
  };
}
